import React from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import { Banner, TextLink } from '@spotify-internal/encore-creator-web';
import { white, screenSmMin } from '@spotify-internal/encore-foundation';

import { useCurrentArtistIdOrNull } from '../../artists';
import { useBanner } from './lib/useBanner';
import { useArtistFundraisingPickSelfServeRollout } from '../utils';
import type { FundraisingLocationState } from './types';

export const FundraisingBannerWrapper = styled.div`
  & > * {
    border-radius: 4px;
  }

  @media (max-width: ${screenSmMin}) {
    width: 100vw;
    position: relative;
    /*
     * Push the container to the exact middle of the browser window with left: 50%
     * then pull it back to the left edge with -50vw margin
     */
    left: 50%;
    right: 50%;
    margin-left: -50vw;
    margin-right: -50vw;

    & > * {
      border-radius: 0;
    }
  }
`;

export function FundraisingBanner() {
  const { done, hide, announcement, success } = useBanner();

  // Once self serve is rolled out, we no longer need to show any announcement banner
  const selfServeRolledOut = useArtistFundraisingPickSelfServeRollout();

  if (hide) return null;

  if (announcement && !selfServeRolledOut) {
    return (
      <FundraisingBannerWrapper>
        <Banner variant={Banner.announcement}>
          <FundraisingAnnouncement />
        </Banner>
      </FundraisingBannerWrapper>
    );
  }

  if (success) {
    return (
      <FundraisingBannerWrapper>
        <Banner variant={Banner.success} onClose={done}>
          <FundraisingSuccess />
        </Banner>
      </FundraisingBannerWrapper>
    );
  }

  return null;
}

const FundraisingBannerLink = styled(TextLink)`
  color: ${white};
  text-decoration: underline;
`;

function FundraisingAnnouncement() {
  const currentArtistId = useCurrentArtistIdOrNull();
  const history = useHistory<FundraisingLocationState>();
  const fundraisingFormHref = currentArtistId
    ? `${window.location.origin}/c/artist/${currentArtistId}/profile/fundraising`
    : '';

  return (
    <>
      You can now add a fundraising link to your profile so your fans can
      support you or your cause directly. No money will go to Spotify.{' '}
      <FundraisingBannerLink
        onClick={(e: React.MouseEvent) => {
          history.push(`/artist/${currentArtistId}/profile/fundraising`);
          e.preventDefault();
        }}
        href={fundraisingFormHref}
      >
        Get started
      </FundraisingBannerLink>
    </>
  );
}

function FundraisingSuccess() {
  return <>Your fundraising link has been received</>;
}
